import { useGoogleAnalyticsContext, useWebStoreContext } from "contexts";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useHandleDeeplinkMutation } from "redux/apis/deeplinkController";
import { onOpenPopUp, onSetDeeplinkState } from "redux/slices";
import { useAppDispatch } from "./useAppDispatch";
import useRewardsById from "./useRewardsById";

interface IRewardingDeeplinkResponse {
	deeplinkId?: string;
	rewardId?: string;
	success: boolean;
	data?: {
		errorCode: string;
		errorMessage: string;
		success: boolean;
	};
}

const LS_SURVIVABLE_DEEPLINK = "store.solitairetripeaks.com:survivableDeeplink";

const useDeeplinkAction = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { pathname } = useLocation();
	const [searchParams] = useSearchParams();
	const [executeAction] = useHandleDeeplinkMutation();
	const { reportEvent, reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const { authToken, userTracker, openLoginToContinueDialog } =
		useWebStoreContext();
	const [data, setData] = useState<IRewardingDeeplinkResponse>();
	const displayRewards = useRewardsById(data?.rewardId as string);

	const deeplink = localStorage.getItem(LS_SURVIVABLE_DEEPLINK) || undefined;

	const cleanAfterDeeplink = useCallback(() => {
		dispatch(onOpenPopUp("rewarding-deeplink-reward"));
		localStorage.removeItem(LS_SURVIVABLE_DEEPLINK);
		navigate(pathname, { replace: true });
	}, []);

	const setAction = async () => {
		const value = {
			id: searchParams.get("deeplinkId")
		};
		if (value.id) {
			await executeAction(value)
				.unwrap()
				.then((res: IRewardingDeeplinkResponse) => {
					setData(res);
					dispatch(
						onSetDeeplinkState({
							isError: false,
							isSuccess: true,
							error: undefined,
							data: res
						})
					);
				})
				.catch((err) => {
					setData(err);
					console.error("Error: ", err);
					dispatch(
						onSetDeeplinkState({
							isError: true,
							isSuccess: false,
							error: err,
							data: undefined
						})
					);
				})
				.finally(() => {
					cleanAfterDeeplink();
				});
		}
	};

	useEffect(() => {
		if (data?.data?.errorCode) {
			reportEvent("rewarding_deeplink_pop_up", {
				source: "rewarding_deeplinks",
				claim_success: data?.data?.success,
				error_code: data?.data?.errorCode,
				error_message: data?.data?.errorMessage
			});
			reportPlaygamiEvent("tp.web_frontend", {
				event_name: "rewarding_deeplink_pop_up",
				event_type: "rewarding_deeplink_pop_up",
				action: "claim_error",
				source: "rewarding_deeplinks",
				error_type: data?.data?.errorCode,
				content: data?.data?.errorMessage
			});
		}
	}, [data?.data?.errorCode]);

	useEffect(() => {
		if (data && displayRewards.length > 0) {
			reportEvent("rewarding_deeplink_pop_up", {
				source: "rewarding_deeplinks",
				claim_success: data?.success,
				reward: displayRewards?.[0]?.id,
				reward_amount: displayRewards?.[0]?.description
			});
			reportPlaygamiEvent("tp.web_frontend", {
				event_name: "rewarding_deeplink_pop_up",
				event_type: "rewarding_deeplink_pop_up",
				action: "claim_success",
				source: "rewarding_deeplinks",
				type: "reward",
				reward_id: displayRewards?.[0]?.id,
				value_int: Number(displayRewards?.[0]?.description?.replace(",", ""))
			});
		}
	}, [data?.rewardId, displayRewards?.[0]?.id]);

	useEffect(() => {
		if (deeplink && authToken?.value) {
			navigate(`${pathname}?deeplinkId=${deeplink}`);
		}
	}, [deeplink, authToken?.value]);

	useEffect(() => {
		const deeplinkId = searchParams.get("deeplinkId");
		if (!!userTracker && deeplinkId) {
			setAction();
		} else if (!authToken?.value && deeplinkId) {
			openLoginToContinueDialog();
			localStorage.setItem(LS_SURVIVABLE_DEEPLINK, deeplinkId);
		}
	}, [userTracker?.id, searchParams.has("deeplinkId")]);
};

export default useDeeplinkAction;
